/**
 * The time dial's motion, as a pure reducer.
 *
 * Nothing here touches the DOM, a clock or an animation frame: the component feeds it elapsed
 * milliseconds and it returns the next state. That is what makes a flick, a detent and a settle
 * testable at all — jsdom has no layout and no frames to measure.
 *
 * The feel is three behaviours, not one spring field. A flick coasts under friction; crossing a
 * detent bleeds a little speed, which is what makes a notch feel notched; and once the dial is
 * slow enough it is pulled home by a critically damped spring. Critically damped on purpose — a
 * dial that wobbles into place reads as loose, and an overshoot would change which recording is
 * selected after the traveller has already read it.
 */

import { localDayOf } from "./days.ts";

/** One full turn is one day, so the rim is a 24-hour clock face and the angle is the time of day. */
export const TURN_MS = 86_400_000;

/**
 * How wide the disk is drawn, in screens. Must match `.dial-disk { width: 300vw }`: the drag
 * gearing is derived from it, so the two cannot be changed independently.
 */
export const DISK_WIDTH_IN_SCREENS = 3;

/**
 * Pixels of finger travel per radian of turn.
 *
 * Set to the disk's own radius, so dragging the rim turns it exactly as far as the finger moved —
 * the dial is grabbed, not nudged. That makes it deliberately slow: a whole day is several
 * screen-widths of dragging, which is the point, because an hour has to be reachable.
 */
export const pxPerRadian = (viewportWidth: number): number =>
  Math.max(1, (DISK_WIDTH_IN_SCREENS / 2) * viewportWidth);
const TAU = Math.PI * 2;

export type Detent = {
  /** Where it sits on the dial, in the same monotonic radians as [DialState.angle]. */
  angle: number;
  /** The instant it stands for. */
  t: number;
  /** How many recordings were merged into it. */
  count: number;
};

export type DialState = {
  /** Monotonic: it keeps growing as the dial turns, it does not wrap at a turn. */
  angle: number;
  /** Radians per second. */
  velocity: number;
  mode: "rest" | "drag" | "coast" | "settle";
};

export type DialOptions = {
  /** Lowest and highest angle the dial can reach — the trip's own bounds. */
  min: number;
  max: number;
  /** Fraction of speed kept per second while coasting. */
  friction: number;
  /** Fraction of speed kept each time a detent is crossed. */
  detentBleed: number;
  /** Below this speed (rad/s) the dial stops coasting and is pulled to the nearest detent. */
  settleSpeed: number;
  /**
   * Whether the detents pull. With it off the dial still marks them, still ticks as they pass
   * and still steps between them by key — it simply stops dragging the reader's thumb to the
   * nearest one, and comes to rest wherever it was left.
   */
  magnetic: boolean;
  /** The settling spring's natural frequency, rad/s. Damping is always critical. */
  spring: number;
  /** Closer than this to the target, and slow enough, the dial is declared at rest. */
  restAngle: number;
  /** The fastest the dial may be thrown, rad/s. A hand cannot beat this; a bad sample can. */
  maxSpeed: number;
  /**
   * Where one local day becomes the next, in the same monotonic radians as [DialState.angle].
   * Read from the zone's own day bounds rather than assumed to be every turn, because the day
   * a clock changes is 23 or 25 hours long and its midnight is not a whole turn from the last.
   */
  boundaries: number[];
  /** How far either side of a boundary the extra resistance is felt, in radians. */
  boundaryZone: number;
  /** At the boundary itself, the fraction of normal travel a finger gets. Lower is stickier. */
  boundaryGrip: number;
  /** Fraction of speed kept each time a boundary is crossed while coasting. */
  boundaryBleed: number;
};

export const DEFAULTS: DialOptions = {
  min: -Infinity,
  max: Infinity,
  // iOS's normal deceleration is 0.998 per millisecond; 0.998^1000 is what that keeps per second.
  // Matching it is what makes a flick glide instead of stopping dead.
  friction: 0.135,
  detentBleed: 0.92,
  // Low on purpose. The glide is the feel, so the spring only takes the last of it — at a high
  // threshold the dial stops gliding early and is yanked home, which reads as snapping.
  settleSpeed: 0.35,
  magnetic: true,
  // Gentler than before to match the longer glide: the dial eases onto a detent rather than
  // arriving at it. Still critically damped, so it never overshoots.
  spring: 9,
  restAngle: 0.0005,
  // Faster than anyone turns a dial, and a flick from here glides about fifteen hours.
  // The cap exists because the velocity estimate divides by the gap between two pointer events,
  // and events that arrive in the same millisecond would otherwise read as an infinite flick.
  maxSpeed: 8,
  boundaries: [],
  // Eighty minutes of dial either side of midnight: far enough out that the resistance is felt
  // coming rather than hit, and the whole approach to the end of a day slows down.
  boundaryZone: 0.35,
  // Hardest gearing at midnight: a finger gets a quarter of its usual travel. Crossing a day by
  // dragging costs about half a screen more than the same distance mid-afternoon — a hill, not
  // a wall, and the day strip above is still one tap for anyone who does not want to climb it.
  boundaryGrip: 0.25,
  // What a flick keeps after getting through. These three together are tuned so that a gentle
  // flick (about 1.6 rad/s) stops just short of midnight and stays in the day it started in,
  // while a firm one (3.5 and up) crosses — and even the hardest throw the cap allows lands in
  // the next day rather than three days away.
  boundaryBleed: 0.45,
};

/**
 * How freely the dial turns at an angle: 1 away from a day boundary, falling to `boundaryGrip`
 * at midnight itself and ramping back out linearly.
 *
 * A day should not change by accident. Without this, a day boundary is just another angle, and
 * an ordinary flick sails through three of them — which makes the one thing a traveller is
 * trying to read, "what happened on Tuesday", the hardest thing to hold still on.
 */
export function grip(angle: number, options: Partial<DialOptions> = {}): number {
  const o = { ...DEFAULTS, ...options };
  if (o.boundaries.length === 0 || o.boundaryZone <= 0) return 1;
  let nearest = Infinity;
  for (const b of o.boundaries) {
    const d = Math.abs(angle - b);
    if (d < nearest) nearest = d;
  }
  if (nearest >= o.boundaryZone) return 1;
  return o.boundaryGrip + (1 - o.boundaryGrip) * (nearest / o.boundaryZone);
}

/**
 * One increment of a drag, after the boundary's resistance has taken its share.
 *
 * Applied to the increment rather than to the finger's total travel, so turning back retraces
 * the same resistance it came through and the dial cannot drift away from the finger over a
 * long gesture.
 */
export function geared(angle: number, delta: number, options: Partial<DialOptions> = {}): number {
  return delta * grip(angle, options);
}

/** The day boundaries strictly between two angles. */
function boundariesBetween(boundaries: number[], from: number, to: number): number {
  const lo = Math.min(from, to);
  const hi = Math.max(from, to);
  let n = 0;
  for (const b of boundaries) if (b > lo && b <= hi) n++;
  return n;
}

/** A step never integrates more than this at once, so a backgrounded tab cannot tunnel. */
const MAX_STEP_MS = 32;

export const nearestDetent = (detents: Detent[], angle: number): Detent | null => {
  if (detents.length === 0) return null;
  let lo = 0;
  let hi = detents.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (detents[mid]!.angle < angle) lo = mid + 1;
    else hi = mid;
  }
  const after = detents[lo]!;
  const before = detents[Math.max(0, lo - 1)]!;
  return Math.abs(after.angle - angle) < Math.abs(angle - before.angle) ? after : before;
};

/** The detents strictly between two angles, in the order they were passed. */
function crossedBetween(detents: Detent[], from: number, to: number): Detent[] {
  const lo = Math.min(from, to);
  const hi = Math.max(from, to);
  const out = detents.filter((d) => d.angle > lo && d.angle <= hi);
  return to < from ? out.reverse() : out;
}

/**
 * One tick. Returns the next state and the detents passed on the way, which the component turns
 * into haptic ticks.
 */
export function step(
  state: DialState,
  dtMs: number,
  detents: Detent[],
  options: Partial<DialOptions> = {},
): { state: DialState; crossed: Detent[] } {
  const o = { ...DEFAULTS, ...options };
  if (state.mode === "rest" || state.mode === "drag" || dtMs <= 0) {
    return { state, crossed: [] };
  }
  // A long gap (a backgrounded tab, a slow frame) is integrated as one short step: the dial
  // carries on from where it was rather than leaping across the trip.
  const dt = Math.min(dtMs, MAX_STEP_MS) / 1000;

  let { angle, velocity, mode } = state;

  if (mode === "coast") {
    // Near a day boundary the same second of coasting costs more speed: dividing the elapsed
    // time by the grip stretches the decay without putting a discontinuity anywhere.
    velocity *= Math.pow(o.friction, dt / grip(angle, o));
    angle += velocity * dt;
    if (Math.abs(velocity) < o.settleSpeed) mode = "settle";
  } else {
    // Critically damped: one spring constant, damping exactly 2·sqrt(k). With the magnet off the
    // target is where the dial already is, so the same spring simply brings it to a stop.
    const target = o.magnetic ? (nearestDetent(detents, angle)?.angle ?? angle) : angle;
    const k = o.spring * o.spring;
    const c = 2 * o.spring;
    velocity += (-k * (angle - target) - c * velocity) * dt;
    angle += velocity * dt;
  }

  // The trip's ends are walls, not springs: hitting one stops the dial rather than ringing it.
  if (angle <= o.min) {
    angle = o.min;
    velocity = 0;
  } else if (angle >= o.max) {
    angle = o.max;
    velocity = 0;
  }

  const crossed = crossedBetween(detents, state.angle, angle);
  // Each notch takes a bite out of the flick. Only while coasting: during the settle the spring
  // owns the motion, and bleeding there would stop the dial short of its target.
  if (mode === "coast" && crossed.length > 0) {
    velocity *= Math.pow(o.detentBleed, crossed.length);
  }
  // A midnight takes a much bigger bite than a recording does, so a flick spends itself on the
  // day it is in rather than skating through several.
  if (mode === "coast") {
    const days = boundariesBetween(o.boundaries, state.angle, angle);
    if (days > 0) velocity *= Math.pow(o.boundaryBleed, days);
  }

  if (mode === "settle") {
    // The same target the spring is pulling towards — which, with the magnet off, is where the
    // dial already is. Measuring against a detent it was never going to reach would mean it
    // never came to rest: it would animate for ever, and a dial that is never at rest ignores
    // the playhead being moved from anywhere else, which is what the day tabs do.
    const target = o.magnetic ? (nearestDetent(detents, angle)?.angle ?? angle) : angle;
    if (Math.abs(angle - target) < o.restAngle && Math.abs(velocity) < o.restAngle * o.spring) {
      return { state: { angle: target, velocity: 0, mode: "rest" }, crossed };
    }
  }
  if (velocity === 0 && (angle === o.min || angle === o.max)) {
    return { state: { angle, velocity: 0, mode: "rest" }, crossed };
  }

  return { state: { angle, velocity, mode }, crossed };
}

/** Let go: whatever speed the finger left behind becomes a coast, within reason. */
export function release(
  state: DialState,
  velocity: number,
  options: Partial<DialOptions> = {},
): DialState {
  const o = { ...DEFAULTS, ...options };
  return { angle: state.angle, velocity: clampSpeed(velocity, o.maxSpeed), mode: "coast" };
}

const clampSpeed = (v: number, max: number) =>
  Number.isFinite(v) ? Math.min(max, Math.max(-max, v)) : 0;

/** Drag to an angle, clamped to the trip. Velocity is the component's smoothed estimate. */
export function dragTo(
  state: DialState,
  angle: number,
  velocity: number,
  options: Partial<DialOptions> = {},
): DialState {
  const o = { ...DEFAULTS, ...options };
  return {
    angle: Math.min(o.max, Math.max(o.min, angle)),
    velocity: clampSpeed(velocity, o.maxSpeed),
    mode: "drag",
  };
}

/** With reduced motion there is no flick and no settle: let go and you are on the nearest detent. */
export function snap(state: DialState, detents: Detent[]): DialState {
  const target = nearestDetent(detents, state.angle)?.angle ?? state.angle;
  return { angle: target, velocity: 0, mode: "rest" };
}

// ---------------------------------------------------------------------------
// Angle, time, and what is engraved on the rim
// ---------------------------------------------------------------------------

/**
 * The angle is the time: one turn is one day, so a quarter turn is six hours wherever you are in
 * the trip. Measured from the trip's start so the numbers stay small and the dial has an origin.
 */
export const angleForTime = (t: number, from: number): number => ((t - from) / TURN_MS) * TAU;
export const timeForAngle = (angle: number, from: number): number => from + (angle / TAU) * TURN_MS;

/**
 * Where the dial is pulled: the moments something was recorded.
 *
 * Clustered, because a burst of two hundred frames in a minute would otherwise put two hundred
 * detents inside a degree — the dial would feel like sandpaper and snapping would mean nothing.
 * A cluster keeps the time of its first recording and remembers how many it stands for.
 */
export function detentsFor(times: number[], from: number, minSpacingRad: number): Detent[] {
  const sorted = [...times].sort((a, b) => a - b);
  const out: Detent[] = [];
  for (const t of sorted) {
    const angle = angleForTime(t, from);
    const last = out[out.length - 1];
    if (last && angle - last.angle < minSpacingRad) {
      last.count += 1;
      continue;
    }
    out.push({ angle, t, count: 1 });
  }
  return out;
}

export type Mark = {
  angle: number;
  t: number;
  kind: "minor" | "hour" | "day";
  /** Set on hour and day marks only. */
  label?: string;
};

const QUARTER_MS = 900_000;

/**
 * The engraving inside one arc of the rim: quarter-hour ticks, labelled hours, and a heavier mark
 * where the local day rolls over.
 *
 * Labels are read from the local time at each angle rather than generated by arithmetic, so the
 * day a clock changes is engraved truthfully: spring forward and an hour of labels is missing,
 * autumn and the repeated hour is engraved twice. The dial itself keeps turning at one day per
 * turn throughout — which is what the traveller's own watch did.
 */
export function marksFor(
  fromAngle: number,
  toAngle: number,
  tripFrom: number,
  timeZone: string,
  locale?: string,
): Mark[] {
  if (!(toAngle > fromAngle)) return [];
  const startMs = timeForAngle(fromAngle, tripFrom);
  const endMs = timeForAngle(toAngle, tripFrom);
  // A guard, not a limit: the visible arc is a few hours, so this is never reached in practice.
  if ((endMs - startMs) / QUARTER_MS > 500) return [];

  const hour = new Intl.DateTimeFormat(locale, { timeZone, hour: "numeric" });
  const day = new Intl.DateTimeFormat(locale, { timeZone, weekday: "short", day: "numeric" });
  const minuteOf = new Intl.DateTimeFormat("en-US", { timeZone, minute: "2-digit" });
  /** The local day — shared with the media library, so the two screens cannot disagree. */
  const localAt = (ms: number) => ({
    day: localDayOf(ms, timeZone),
    minute: Number(minuteOf.format(new Date(ms))),
  });

  const out: Mark[] = [];
  const first = Math.ceil(startMs / QUARTER_MS) * QUARTER_MS;
  let previousDay = localAt(first - QUARTER_MS).day;
  for (let ms = first; ms <= endMs; ms += QUARTER_MS) {
    const angle = angleForTime(ms, tripFrom);
    const here = localAt(ms);
    if (here.day !== previousDay) {
      out.push({ angle, t: ms, kind: "day", label: day.format(new Date(ms)) });
      previousDay = here.day;
      continue;
    }
    // The hour comes from the local clock, never from `ms % HOUR_MS`: a zone offset by half an
    // hour (Kolkata) or three quarters (Kathmandu) would otherwise have its hours engraved on
    // the wrong ticks.
    out.push(
      here.minute === 0
        ? { angle, t: ms, kind: "hour", label: hour.format(new Date(ms)) }
        : { angle, t: ms, kind: "minor" },
    );
  }
  return out;
}
